import { useState, useRef, useEffect, useContext } from "react"; // Added useRef to handle file input reference
import {
  Button,
  Form,
  Row,
  Col,
  Spin,
  Select,
  Typography,
  List,
  Card,
} from "antd";
import {
  UploadOutlined,
  CloseCircleFilled,
  CheckCircleOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import AuthUtil from "../../../utils/auth";
import {
  TContext,
  TDropdownOptions,
  TResponseModel,
  TUser,
} from "../../../types/config";
import {
  GetCustomerList,
  ImportCustomerUploadedList,
} from "../../../utils/api.constant";
import { eHTTPStatusCode, eResultCode } from "../../../utils/enum";
import { TBulkUploadResponse } from "../../../types";
import BulkUploadApprovalList from "./bulk-upload-approval-list";
import { STAKE_HOLDERS, UNAUTHORIZEDREQUEST } from "../../../utils/constants";
import Utils from "../../../utils";
import BulkUploadSuccess from "./bulk-upload-success";
import { UserContext } from "../../../context";

type DrawerProps = {
  onCloseDrawer: () => void;
  onRefreshList: () => void;
  orgType?: string;
};

/**
 * Component for handling bulk upload functionality.
 * Allows users to upload XLSX files and process the data.
 */
export default function BulkUploadForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [file, setFile] = useState<File | null>(null); // Store the uploaded file
  const fileInputRef = useRef<HTMLInputElement | null>(null); // Ref to handle file input
  const [selectedName, setSelectedName] = useState<string>("");
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  const [bulkUploadResponse, setBulkUploadResponse] =
    useState<TBulkUploadResponse>();
  const context: TContext = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser | undefined>(
    context?.user as TUser
  );

  useEffect(() => {
    fetchCustomerOptions();
  }, []);

  useEffect(() => {
    setActiveUser(context?.user);
    if (context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      form.setFieldValue("customerId", context?.user?.orgId);
      setSelectedName(context?.user?.orgName || "");
    }
  }, [context?.user]);

  const fetchCustomerOptions = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetCustomerList, requestPayload);
      const { dataResponse, data } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        // Transform data to match table columns
        const custOptions = Utils.mapDropdownOptions(data);
        setCustomerOptions(custOptions);
        if (activeUser?.stakeHolderName === STAKE_HOLDERS.IDEX) {
          form.setFieldValue("customerId", custOptions?.[0]?.value);
          setSelectedName(custOptions?.[0]?.label)
        }
        setIsLoading(false);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  /**
   * Handles file selection from the input.
   * Validates the file type and updates the state accordingly.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the file input
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!form.getFieldValue("customerId")) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Please fill all the mandatory fields",
      });
      return;
    }
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      // const isXlsx =
      //   selectedFile.type ===
      //   "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      // if (!isXlsx) {
      //   onShowToast({
      //     type: ToastType.error,
      //     title: <CloseCircleFilled />,
      //     position: ToastOpen.leftBottom,
      //     content: "Please upload an XLSX file!",
      //   });
      //   setFile(null); // Reset file state
      //   event.target.value = ""; // Clear the input
      // } else {
      //   setFile(selectedFile); // Set the selected file
      //   handleSubmit(selectedFile);
      // }
      setFile(selectedFile); // Set the selected file
      handleSubmit(selectedFile);
    }

    // Reset the input value to ensure `onChange` is triggered for the same file
    event.target.value = "";
  };

  /**
   * Handles the submission of the bulk upload form.
   * Validates the file and if valid, sends a POST request to the server to process the file.
   * If the response is successful, refreshes the list and closes the drawer.
   * If the response is not successful, shows an error toast with the error message.
   */
  const handleSubmit = async (file: any) => {
    if (!file) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Please select an XLSX file!",
      });
      return;
    }

    // Implement your file upload logic here
    const formData = new FormData();
    formData.append("file", file);
    formData.append("customerId", form.getFieldValue("customerId"));

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${ImportCustomerUploadedList}`,
        {
          method: "POST",
          body: formData,

          headers: {
            ...AuthUtil.getToken(),
          },
        }
      );
      if (response.status === eHTTPStatusCode.OK) {
        const responseData = (await response.json()) as TResponseModel;
        if (responseData.dataResponse.returnCode === eResultCode.SUCCESS) {
          setShowSummary(true);
          onShowToast({
            type: ToastType.success,
            title: <CheckCircleFilled />,
            position: ToastOpen.leftBottom,
            content: responseData.dataResponse.description,
          });
          props.onRefreshList();
          setBulkUploadResponse({
            errorCode: responseData.dataResponse.returnCode,
            errorMessage: responseData.dataResponse.description,
            customerName: selectedName,
            ...responseData.data,
          });
          // props.onCloseDrawer();
        } else {
          setShowSummary(true);
          setBulkUploadResponse({
            errorCode: responseData.dataResponse.returnCode,
            errorMessage: responseData.dataResponse.description,
            customerName: selectedName,
            ...responseData.data,
          });
        }
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: UNAUTHORIZEDREQUEST,
        });
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Error uploading file",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const contentStyle: React.CSSProperties = {
    padding: 50,
  };

  const content = <div style={contentStyle} />;
  return (
    <>
      <Form
        onFinish={handleSubmit}
        layout="vertical"
        name="bulkUploadForm"
        form={form}
        autoComplete="off"
      >
        <Row
          gutter={[16, 16]}
          style={{
            display: "flex",
            flexWrap: "wrap",
          }}
        >
          <Col xs={24} sm={24} md={24} lg={11} xl={11}>
            <Card title={<h4>Upload Instructions</h4>} bordered={false}>
              <List>
                <List.Item>
                  <Typography.Text>
                    <CheckCircleOutlined style={{ color: "#52c41a" }} /> The
                    file being uploaded must contain all the headers mentioned
                    below with exact same titles. <br />
                    (Record ID #, PO Org, Part #, Part - PO Cost, Serial Number,
                    Debrief Date, SA Subject, Ship Tracking #, Fedex Date, Fedex
                    Signed, Installation Date).
                  </Typography.Text>
                </List.Item>
                <List.Item>
                  <Typography.Text>
                    <CheckCircleOutlined style={{ color: "#52c41a" }} /> Serial
                    number should not be blank.
                  </Typography.Text>
                </List.Item>
                <List.Item>
                  <Typography.Text>
                    <CheckCircleOutlined style={{ color: "#52c41a" }} /> While
                    uploading an excel file, Debrief date, Fedex date &
                    Installation date may be blank. But if entered, these dates
                    should preferably be in excel date format. If entered as
                    string, the format accepted is MM-DD-YY.
                  </Typography.Text>
                </List.Item>
                <List.Item>
                  <Typography.Text>
                    <CheckCircleOutlined style={{ color: "#52c41a" }} /> While
                    uploading a csv file, Debrief date, Fedex date &
                    Installation date may be blank. But if entered, for these
                    dates, the formats accepted are YYYY-MM-DD 00:00:00+00:00
                    and MM-DD-YYYY 00:00:00+00:00.
                  </Typography.Text>
                </List.Item>
                <List.Item>
                  <Typography.Text>
                    <CheckCircleOutlined style={{ color: "#52c41a" }} /> Part -
                    PO Cost if entered should be in excel numeric or currency
                    format.
                  </Typography.Text>
                </List.Item>
              </List>
            </Card>
          </Col>
          <Col span={1}>
            <div
              style={{
                height: "100%",
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            {showSummary &&
            bulkUploadResponse?.errorCode !== eResultCode.SUCCESS ? (
              <BulkUploadApprovalList
                backToUpload={() => {
                  setShowSummary(false);
                  setFile(null);
                  setBulkUploadResponse(undefined);
                }}
                bulkUploadResponse={bulkUploadResponse as TBulkUploadResponse}
              ></BulkUploadApprovalList>
            ) : showSummary &&
              bulkUploadResponse?.errorCode === eResultCode.SUCCESS ? (
              <BulkUploadSuccess
                customerName={bulkUploadResponse?.customerName}
                message={bulkUploadResponse?.errorMessage}
                onBackToUpload={() => {
                  setShowSummary(false);
                  setFile(null);
                  setBulkUploadResponse(undefined);
                }}
              />
            ) : (
              <>
                {/* <h3>Upload an Excel</h3> */}
                <Row
                  gutter={16}
                  className="formRowHeight"
                  style={{ marginTop: "10px" }}
                >
                  <Col span={24}>
                    <Form.Item
                      name="customerId"
                      label="Customer"
                      rules={[
                        { required: true, message: "Please choose customer" },
                      ]}
                    >
                      <Select
                        size="large"
                        disabled={
                          isLoading ||
                          activeUser?.stakeHolderName === STAKE_HOLDERS.CUSTOMER
                            ? true
                            : false
                        }
                        showSearch
                        placeholder="Select a customer"
                        options={customerOptions}
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        onChange={(value, option) => {
                          const selectedOption = option as {
                            label: string;
                            value: string;
                          };
                          setSelectedName(selectedOption.label);
                        }}
                      ></Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16} className="formRowHeight">
                  {isLoading ? (
                    <Col
                      span={24}
                      style={{
                        justifyContent: "center",
                        display: "flex",
                        marginTop: "1rem",
                        flexDirection: "column",
                      }}
                    >
                      <Spin
                        tip={
                          <span
                            style={{
                              fontFamily: "Montserrat-Regular",
                            }}
                          >
                            Validating . . .
                          </span>
                        }
                        size="large"
                      >
                        {content}
                      </Spin>
                    </Col>
                  ) : (
                    <Col
                      span={24}
                      style={{
                        justifyContent: "flex-start",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Form.Item label="" style={{ marginBottom: 0 }}>
                        <input
                          type="file"
                          accept=".xlsx"
                          onChange={handleFileChange}
                          ref={fileInputRef}
                          style={{ display: "none" }} // Hide the input
                        />
                        <Button
                          disabled={
                            isLoading || !form.getFieldValue("customerId")
                          }
                          onClick={triggerFileInput}
                          loading={isLoading}
                          icon={<UploadOutlined />}
                          size="large"
                          style={{
                            width: "100%",
                            textOverflow: "ellipsis", // Truncate text
                            overflow: "hidden", // Hide overflow content
                            whiteSpace: "nowrap", // Prevent wrapping to a new line
                          }}
                        >
                          {file ? (
                            <span
                              style={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {file.name}
                            </span>
                          ) : (
                            "Choose to Upload"
                          )}
                        </Button>
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </>
            )}
          </Col>
        </Row>
      </Form>
    </>
  );
}
